import React, { useState } from "react";
import { Box, Chip, CircularProgress, Stack, Tooltip, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ColorizedLogText from "./ColorizedLogText";
import { HighlightedText } from "../preview/searchUtils";
import {
  LOG_STATE_CHIP_COLOR,
  LOG_STATE_COLORS,
  LogRecordDto,
  formatLogDate,
  formatLogTime,
  recordSummary,
  recordTitle,
} from "./logRecords";

const STATE_LABELS: Record<LogRecordDto["state"], string> = {
  success: "SUCCESS",
  error: "ERROR",
  warning: "WARNING",
  unknown: "UNKNOWN",
};

function recordKey(record: LogRecordDto, index: number): string {
  return [
    record.stream,
    record.timestamp,
    record.employee_id ?? record.employee_name ?? "",
    record.filename ?? "",
    index,
  ].join("|");
}

/**
 * One row per record: what it is, what state it is in now, and - on click - the
 * exact text the bot wrote. A row that says SUCCESS while carrying a RESOLVED
 * badge is an employee (or a file) that failed earlier and was fixed since; the
 * badge is what stops that history from reading as a live problem.
 */
function LogRecordRow({
  record,
  query,
  showTask,
}: {
  record: LogRecordDto;
  query: string;
  showTask: boolean;
}) {
  const [open, setOpen] = useState(false);
  const title = recordTitle(record);
  const summary = recordSummary(record);
  const stateColor = LOG_STATE_COLORS[record.state];

  return (
    <Box
      sx={{
        borderBottom: 1,
        borderColor: "divider",
        px: 1.5,
        py: 0.75,
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          cursor: "pointer",
          minWidth: 0,
          flexWrap: { xs: "wrap", sm: "nowrap" },
          rowGap: 0.5,
        }}
        onClick={() => setOpen((prev) => !prev)}
      >
        <Box sx={{ color: "text.disabled", display: "flex", alignItems: "center" }}>
          {open ? (
            <ExpandMoreIcon fontSize="small" />
          ) : (
            <ChevronRightIcon fontSize="small" />
          )}
        </Box>
        <Tooltip title={record.level || "(no level)"}>
          <Chip
            size="small"
            label={STATE_LABELS[record.state]}
            color={LOG_STATE_CHIP_COLOR[record.state]}
            variant="outlined"
            sx={{ height: 20, minWidth: 78, fontSize: "0.68rem", fontWeight: 600 }}
          />
        </Tooltip>
        <Tooltip title={formatLogDate(record.timestamp) || "(no date)"}>
          <Typography
            variant="caption"
            sx={{ fontFamily: "monospace", color: "text.secondary", flexShrink: 0 }}
          >
            {formatLogTime(record.timestamp)}
          </Typography>
        </Tooltip>
        <Box
          sx={{
            minWidth: 0,
            flexGrow: 1,
            // Once the row wraps, the title owns its own line and the badges move
            // below it, instead of the badges pushing the title out of existence.
            flexBasis: { xs: "100%", sm: "auto" },
            order: { xs: 3, sm: 0 },
          }}
        >
          <Tooltip title={title}>
            <Typography
              variant="body2"
              sx={{
                color: stateColor,
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              <HighlightedText text={title} query={query} />
            </Typography>
          </Tooltip>
          {summary ? (
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              <HighlightedText text={summary} query={query} />
            </Typography>
          ) : null}
        </Box>
        <Stack
          direction="row"
          sx={{
            flexShrink: 0,
            flexWrap: "wrap",
            gap: 0.5,
            minWidth: 0,
            order: { xs: 4, sm: 0 },
          }}
        >
          {record.inner_number != null ? (
            <Tooltip title="Employee number in the roster">
              <Chip
                size="small"
                variant="outlined"
                label={`#${record.inner_number}`}
                sx={{ height: 20, fontSize: "0.68rem" }}
              />
            </Tooltip>
          ) : null}
          {record.employee_id ? (
            <Tooltip title="Employee id">
              <Chip
                size="small"
                variant="outlined"
                label={record.employee_id}
                sx={{ height: 20, fontSize: "0.68rem" }}
              />
            </Tooltip>
          ) : null}
          {record.resolved ? (
            <Tooltip
              title={
                record.first_failed_at
                  ? `Failed at ${record.first_failed_at}, fixed by a later run`
                  : "Failed earlier, fixed by a later run"
              }
            >
              <Chip
                size="small"
                color="success"
                label="RESOLVED"
                sx={{ height: 20, fontSize: "0.68rem", fontWeight: 600 }}
              />
            </Tooltip>
          ) : null}
          {record.attempts > 1 ? (
            <Tooltip title={`${record.attempts} attempts on this employee/file`}>
              <Chip
                size="small"
                variant="outlined"
                label={`x${record.attempts}`}
                sx={{ height: 20, fontSize: "0.68rem" }}
              />
            </Tooltip>
          ) : null}
          {showTask && record.from_deleted_task && record.source_task_id != null ? (
            <Tooltip
              title={
                record.owner_task_id != null
                  ? `Written by task ${record.source_task_id}, which was deleted; those employees belong to task ${record.owner_task_id} now`
                  : `Written by task ${record.source_task_id}, which was deleted - its work still counts for the job`
              }
            >
              <Chip
                size="small"
                variant="outlined"
                label={`deleted task ${record.source_task_id}`}
                sx={{
                  height: 20,
                  fontSize: "0.68rem",
                  color: "text.disabled",
                  borderStyle: "dashed",
                }}
              />
            </Tooltip>
          ) : showTask && record.owner_task_id != null ? (
            <Tooltip
              title={
                record.source_task_id != null &&
                record.source_task_id !== record.owner_task_id
                  ? `Owned by task ${record.owner_task_id} now, written by task ${record.source_task_id}`
                  : `Task ${record.owner_task_id}`
              }
            >
              <Chip
                size="small"
                variant="outlined"
                label={`task ${record.owner_task_id}`}
                sx={{ height: 20, fontSize: "0.68rem" }}
              />
            </Tooltip>
          ) : null}
        </Stack>
      </Stack>
      {open ? (
        <Box
          component="pre"
          sx={{
            m: 0,
            mt: 0.75,
            ml: 4,
            p: 1,
            bgcolor: "action.hover",
            borderRadius: 1,
            fontFamily: "monospace",
            fontSize: "0.74rem",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          <ColorizedLogText text={record.raw} />
        </Box>
      ) : null}
    </Box>
  );
}

export default function LogRecordList({
  records,
  query,
  showTask,
  loading,
  emptyLabel,
}: {
  records: LogRecordDto[];
  query: string;
  showTask: boolean;
  loading: boolean;
  emptyLabel: string;
}) {
  if (loading && records.length === 0) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ height: "100%", py: 4 }}>
        <CircularProgress size={22} />
      </Stack>
    );
  }
  if (records.length === 0) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ height: "100%", py: 4 }}>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {emptyLabel}
        </Typography>
      </Stack>
    );
  }
  return (
    <Box>
      {records.map((record, index) => (
        <LogRecordRow
          key={recordKey(record, index)}
          record={record}
          query={query}
          showTask={showTask}
        />
      ))}
    </Box>
  );
}
